import {
  createRef,
  RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import classNames from "classnames"

import { ThreeDotsLoader } from "@atoms"
import Loader from "components/loader"

import { ReactComponent as SendIcon } from "assets/icons/send.svg"
import { ReactComponent as ArrowLink } from "assets/icons/arrow-link.svg"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"

import {
  IMessage,
  useDiscussion,
  useDiscussionInfo,
  useSendMessage,
} from "./queries"

import styles from "./styles.module.scss"
import useNewMessageForm from "./use-form"
import DiscussionHeader from "./discussion-header"
import MessageDetails from "./message-details"
import { useTranslation } from "react-i18next"
import { Button, Input } from "antd"
import { Controller } from "react-hook-form"
import Layout from "modules/layout/dashboard"
import { useMemberId } from ".."

const { Main } = Layout

const Discussion: React.FC = () => {
  const { t } = useTranslation("suite/home")
  const [isScrolled, setIsScrolled] = useState<boolean>(true)
  const [replyingToMsg, setReplyingToMsg] = useState<IMessage>()

  const id = useMemberId()
  const discussionHeader = useDiscussionInfo(id!)
  const discussion = useDiscussion(discussionHeader.data?.id!, {
    enabled: !!discussionHeader.data?.id,
  })
  const { handleSubmit, reset, control } = useNewMessageForm()
  const sendMessage = useSendMessage({
    retry: 3,
  })

  const endMessagesAreaRef = useRef<HTMLLIElement>(null)
  const startMessagesAreaRef = useRef<HTMLLIElement>(null)
  const messagesAreaRef = useRef<HTMLUListElement>(null)
  const canScroll = useRef(true)

  useLayoutEffect(() => {
    // Force scroll to the bottom only onMount
    if (endMessagesAreaRef.current && canScroll.current) {
      endMessagesAreaRef.current.scrollIntoView({})
      canScroll.current = false
    }
  }, [discussion.data])

  useEffect(() => {
    if (!startMessagesAreaRef.current) return
    let options = {
      root: messagesAreaRef.current,
      rootMargin: "0px",
      threshold: 1.0,
    }

    let observer = new IntersectionObserver((entries, observer) => {
      setIsScrolled(!entries[0].isIntersecting)
    }, options)
    observer.observe(startMessagesAreaRef.current!)

    return () => observer.disconnect()
  }, [discussion.data])

  const messages = useMemo(() => {
    return discussion.data?.pages.flatMap((page) => page.data)?.reverse()
  }, [discussion.data])

  const refsById = useMemo(() => {
    const refs: {
      [x: string]: RefObject<HTMLDivElement>
    } = {}
    messages?.forEach((message) => {
      refs[message.id] = createRef()
    })
    return refs
  }, [messages])

  const onViewOlderMessages = async () => {
    if (isScrolled) {
      messagesAreaRef.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      })
      return
    }
    messagesAreaRef.current?.scrollTo({
      top: 10,
      behavior: "smooth",
    })
    discussion.hasNextPage && (await discussion.fetchNextPage())
  }

  const onSubmit = handleSubmit((data) => {
    sendMessage.mutate({
      message_id: "new-message-" + Math.random() * 1000,
      discussion_id: discussionHeader.data?.id!,
      body: data.message,
      reply_to_msg: replyingToMsg,
    })
    setReplyingToMsg(undefined)
    reset()
    setTimeout(
      () => endMessagesAreaRef.current?.scrollIntoView({ behavior: "smooth" }),
      100
    )
  })

  if (!discussion.data) return <Loader size={12} />

  return (
    <Main>
      <div className={styles.details}>
        {!!discussionHeader?.data && (
          <DiscussionHeader discussionHeader={discussionHeader?.data} />
        )}
        <div className={styles.body}>
          <ul className={styles.messages} ref={messagesAreaRef}>
            {!isScrolled && !discussion.hasNextPage ? null : (
              <li className={styles.viewOlderMessages}>
                <span className={classNames("body-heavy", styles.content)}>
                  {discussion.isFetchingNextPage && (
                    <span className={styles.loader}>
                      <ThreeDotsLoader size={8} />
                    </span>
                  )}
                  <span
                    className={classNames(styles.text, {
                      [styles["text--hidden"]]: discussion.isFetchingNextPage,
                    })}
                    onClick={
                      discussion.isFetchingNextPage
                        ? undefined
                        : onViewOlderMessages
                    }
                  >
                    {t("discussion.view_older_messages")}
                    <ArrowLink
                      className={classNames("small", {
                        [styles.hidden]: !isScrolled,
                      })}
                    />
                  </span>
                </span>
              </li>
            )}
            <li aria-hidden ref={startMessagesAreaRef} />
            {messages?.map((message, index) => (
              <div key={message.id} ref={refsById[message.id]}>
                <MessageDetails
                  message={message}
                  sendMessage={sendMessage}
                  setReplyingToMsg={(msg) => setReplyingToMsg(msg)}
                  messagesAreaRef={messagesAreaRef}
                  refsById={refsById}
                  key={index}
                />
              </div>
            ))}
            <li ref={endMessagesAreaRef} aria-hidden />
          </ul>
        </div>
        {replyingToMsg && (
          <div
            className={styles.replyingTo}
            onClick={() =>
              messagesAreaRef.current?.scrollTo({
                top: refsById[replyingToMsg.id].current?.offsetTop! - 10,
                behavior: "smooth",
              })
            }
          >
            <div className={styles.content}>
              <p className="caption-heavy">
                {t("discussion.replying_to")} {replyingToMsg?.creator.name}
              </p>
              <p>{replyingToMsg?.body}</p>
            </div>
            <Button
              type="text"
              onClick={(event) => {
                event.stopPropagation()
                setReplyingToMsg(undefined)
              }}
            >
              <CloseIcon className="small" />
            </Button>
          </div>
        )}
        <form onSubmit={onSubmit}>
          <div className={styles.newMessage}>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <Input
                  bordered={false}
                  placeholder={t("discussion.start_typing")}
                  size="large"
                  className={styles.inputWrapper}
                  autoComplete="off"
                  {...field}
                />
              )}
            />
            <Button
              htmlType="submit"
              type="text"
              shape="circle"
              icon={
                <SendIcon style={{ fill: "var(--color-primary)" }} width={22} />
              }
              size="large"
            />
          </div>
        </form>
      </div>
    </Main>
  )
}

export default Discussion
